import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { 
  ChevronDown, 
  ChevronRight, 
  FileText, 
  Download, 
  Search,
  Clock,
  Shield,
  Users,
  CreditCard,
  AlertTriangle
} from 'lucide-react';
import PageLayout from '../../components/ui/PageLayout';
import Icon from '../../components/AppIcon';


const TermsOfService = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  const termsData = {
    lastUpdated: 'January 10, 2025',
    effectiveDate: 'January 10, 2025',
    version: '1.0',
    sections: [
      {
        id: 'acceptance',
        title: 'Acceptance of Terms',
        icon: Users,
        summary: 'By using LeadGen Pro, you agree to these terms and conditions.',
        content: `
          By accessing and using the LeadGen Pro platform ("Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
          
          These Terms of Service constitute a legally binding agreement between you and LeadGen Pro. Your continued use of the Service confirms your acceptance of these terms.
        `
      },
      {
        id: 'service-usage',
        title: 'Service Usage and Restrictions',
        icon: Shield,
        summary: 'Guidelines for proper use of our lead generation and automation platform.',
        content: `
          **Permitted Use:**
          - Use the Service for legitimate business lead generation and customer relationship management
          - Access and use features according to your subscription plan
          - Export your data in accordance with our data portability policies
          
          **Prohibited Activities:**
          - Use the Service for any unlawful or fraudulent purposes
          - Attempt to gain unauthorized access to other users' accounts or data
          - Send spam, unsolicited messages, or engage in any form of harassment
          - Use the Service to collect or harvest personal information without consent
          - Reverse engineer, decompile, or attempt to extract source code
          - Violate any applicable laws, regulations, or third-party rights
        `
      },
      {
        id: 'subscription',title: 'Subscription Terms and Billing',icon: CreditCard,summary: 'Payment terms, billing cycles, and subscription management policies.',
        content: `
          **Subscription Plans:**
          We offer various subscription plans with different features and usage limits. Your access to features is determined by your active subscription plan.
          
          **Billing and Payment:**
          - Subscriptions are billed in advance on a monthly or annual basis
          - All fees are non-refundable except as required by law
          - We reserve the right to change pricing with 30 days' notice
          - Failed payments may result in service suspension or termination
          
          **Cancellation:**
          You may cancel your subscription at any time. Cancellation will take effect at the end of your current billing period.
        `
      },
      {
        id: 'data-usage',
        title: 'Data Usage and Ownership',
        icon: FileText,
        summary: 'How we handle your data, your rights, and our data processing practices.',
        content: `
          **Your Data:**
          You retain ownership of all data you upload, create, or generate through the Service. We process your data solely to provide and improve our services.
          
          **Data Processing:**
          - We may process your data to provide AI-powered features and analytics
          - Data is stored securely using industry-standard encryption
          - We do not sell your personal data to third parties
          - Data retention periods are outlined in our Privacy Policy
          
          **Data Portability:**
          You have the right to export your data at any time through our data export features.
        `
      },
      {
        id: 'intellectual-property',
        title: 'Intellectual Property Rights',
        icon: Shield,
        summary: 'Ownership of platform features, content, and user-generated data.',
        content: `
          **Our Intellectual Property:**
          The Service, including all content, features, and functionality, is owned by LeadGen Pro and protected by intellectual property laws.
          
          **Your Content:**
          You retain rights to your content but grant us a limited license to use, process, and display it as necessary to provide the Service.
          
          **Trademarks:**
          LeadGen Pro and our logos are trademarks. You may not use them without our written permission.
        `
      },
      {
        id: 'limitation-liability',
        title: 'Limitation of Liability',
        icon: AlertTriangle,
        summary: 'Legal disclaimers and limitations on our liability for service use.',
        content: `
          **Service Availability:**
          While we strive for high availability, we cannot guarantee uninterrupted access to the Service. We provide the Service "as is" without warranties.
          
          **Limitation of Damages:**
          To the maximum extent permitted by law, LeadGen Pro shall not be liable for any indirect, incidental, special, consequential, or punitive damages.
          
          **Maximum Liability:**
          Our total liability to you for any claims related to the Service shall not exceed the amount you paid us in the 12 months preceding the claim.
        `
      },
      {
        id: 'termination',
        title: 'Termination Conditions',
        icon: Clock,
        summary: 'Circumstances under which service may be terminated by either party.',
        content: `
          **Termination by You:**
          You may terminate your account and subscription at any time through your account settings or by contacting support.
          
          **Termination by Us:**
          We may suspend or terminate your account for violations of these terms, non-payment, or other legitimate business reasons.
          
          **Effect of Termination:**
          Upon termination, your access to the Service will cease, and we may delete your data according to our data retention policies.
        `
      }
    ]
  };

  const filteredSections = termsData?.sections?.filter(section =>
    section?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    section?.content?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  ) || [];

  const handlePrintTerms = () => {
    window.print();
  };

  return (
    <>
      <Helmet>
        <title>Terms of Service | LeadGen Pro</title>
        <meta name="description" content="Read LeadGen Pro's Terms of Service covering usage guidelines, subscription terms, and legal obligations for our lead generation platform." />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <PageLayout>
        {/* Hero Section */}
        <div className="bg-white border-b rounded-lg shadow-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-center mb-6"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <FileText className="w-8 h-8 text-blue-600" />
                </div>
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-4xl font-bold text-gray-900 mb-4"
              >
                Terms of Service
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-xl text-gray-600 mb-6"
              >
                Comprehensive legal terms governing your use of LeadGen Pro
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-gray-500"
              >
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Last Updated: {termsData?.lastUpdated}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span>Version {termsData?.version}</span>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Search and Actions */}
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between mb-8">
          {/* Search Bar */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search terms..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value || '')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handlePrintTerms}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              Print/Download
            </button>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="bg-blue-50 rounded-lg p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Table of Contents</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {termsData?.sections?.map((section, index) => (
              <a
                key={section?.id}
                href={`#${section?.id}`}
                className="flex items-center gap-2 text-blue-700 hover:text-blue-900 py-1 text-sm transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
                {index + 1}. {section?.title}
              </a>
            ))}
          </div>
        </div>

        {/* Terms Content */}
        <div className="bg-white rounded-lg shadow-sm border">
          {filteredSections?.map((section, index) => {
            const Icon = section?.icon;
            const isExpanded = expandedSections?.[section?.id];
            
            return (
              <motion.div
                key={section?.id}
                id={section?.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="border-b last:border-b-0"
              >
                <div
                  className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleSection(section?.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                          {section?.title}
                        </h3>
                        <p className="text-gray-600 text-sm">
                          {section?.summary}
                        </p>
                      </div>
                    </div>
                    <div className="ml-4">
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="px-6 pb-6"
                  >
                    <div className="ml-14 prose prose-gray max-w-none">
                      <div className="whitespace-pre-line text-gray-700 leading-relaxed">
                        {section?.content}
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Contact Section */}
        <div className="bg-blue-50 rounded-lg p-6 mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Questions About These Terms?
          </h3>
          <p className="text-gray-600 mb-4">
            If you have any questions about these Terms of Service, please contact our legal team:
          </p>
          <div className="space-y-2 text-sm">
            <p>
              <span className="font-medium">Email:</span>{' '}
              <a 
                href="mailto:legal@leadgenproai.us" 
                className="text-blue-600 hover:text-blue-700"
              >
                legal@leadgenproai.us
              </a>
            </p>
            <p>
              <span className="font-medium">Support:</span>{' '}
              <a 
                href="mailto:support@leadgenproai.us" 
                className="text-blue-600 hover:text-blue-700"
              >
                support@leadgenproai.us
              </a>
            </p>
            <p>
              <span className="font-medium">Phone:</span>{' '}
              <a 
                href="tel:8449781051" 
                className="text-blue-600 hover:text-blue-700"
              >
                (844) 978-1051
              </a>
            </p>
          </div>
        </div>
      </PageLayout>
    </>
  );
};

export default TermsOfService;